import { useState } from "react"
import { Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import { usePoints } from "../../redux/pointsSlice"
import Spinner from "../widgets/Spinner"
import { PointsOrigin } from "../../api/models"
import { usePointTopup, usePointTopups } from "../../redux/pointTopupsSlice"
import { usePointSpending, usePointSpendings } from "../../redux/pointSpendingsSlice"

const pointsOriginLabels: Record<PointsOrigin, string> = {
  [PointsOrigin.Order]: "Store Order",
  [PointsOrigin.Subscription]: "Subscription",
  [PointsOrigin.Code]: "Code Redemption",
  [PointsOrigin.Grant]: "Grant",
}

function PointTopupRow({ id, accessToken }: { id: string; accessToken: string }) {
  const { data: topup } = usePointTopup(id, accessToken)
  if (!topup) return <tr><td colSpan={4}><Spinner size={16} /></td></tr>

  const expired = topup.expiresAt != null && new Date(topup.expiresAt).getTime() < Date.now()
  const availableAt = new Date(topup.availableAt)
  const pending = !expired && availableAt.getTime() > Date.now()

  let originCell: React.ReactElement
  switch (topup.origin) {
    case PointsOrigin.Order:
      originCell = <td><Link to={`/orders/${topup.refId}`} className="card-link">{pointsOriginLabels[topup.origin]}</Link></td>
      break
    case PointsOrigin.Code:
      originCell = <td><Link to={`/redemptions/${topup.refId}`} className="card-link">{pointsOriginLabels[topup.origin]}</Link></td>
      break
    default:
      originCell = <td>{pointsOriginLabels[topup.origin] ?? topup.origin}</td>
      break
  }

  return (
    <tr>
      <td>{new Date(topup.createdAt).toLocaleDateString()}</td>
      {originCell}
      <td style={expired ? { color: "var(--nfa-orange)" } : undefined}>
        {topup.expiresAt ? new Date(topup.expiresAt).toLocaleDateString() : "—"}
        {expired ? <span className="badge-expired">EXPIRED</span> : null}
      </td>
      <td
        className="text-right topup-amount"
        style={expired ? { color: "var(--nfa-orange)", textDecoration: "line-through" } : pending ? { color: "var(--nfa-light-grey)" } : undefined}
        title={pending ? `Available from ${availableAt.toLocaleDateString()}` : undefined}
      >
        {pending ? <span className="badge-pending">Pending</span> : null}
        {topup.amount}
      </td>
    </tr>
  )
}

function PointSpendingRow({ id, accessToken }: { id: string; accessToken: string }) {
  const { data: spending } = usePointSpending(id, accessToken)
  if (!spending) return <tr><td colSpan={3}><Spinner size={16} /></td></tr>

  const typeCell = spending.type === "order"
    ? <td><Link to={`/orders/${spending.refId}`} className="card-link">Store Order</Link></td>
    : <td>{spending.type}</td>

  return (
    <tr>
      <td>{new Date(spending.spentAt).toLocaleDateString()}</td>
      {typeCell}
      <td className="text-right topup-amount" style={{ color: "var(--nfa-orange)" }}>
        {spending.amount}
      </td>
    </tr>
  )
}

function PointTopupsTable({ accessToken }: { accessToken: string }) {
  const { ids, haveMore, loading, more } = usePointTopups(accessToken)

  return (
    <>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Origin</th>
              <th>Expires</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            {ids === undefined ? (
              <tr><td colSpan={4}><Spinner size={16} /></td></tr>
            ) : ids.length === 0 ? (
              <tr><td colSpan={4}>You have not earned any points yet</td></tr>
            ) : (
              ids.map((id) => <PointTopupRow key={id} id={id} accessToken={accessToken} />)
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginLeft: "8px" }}>
        {haveMore && ids !== undefined && (
          loading ? <Spinner size={16} /> : (
            <button className="card-link" onClick={more} style={{ background: "none", border: "none", cursor: "pointer" }}>
              Load more
            </button>
          )
        )}
      </div>
    </>
  )
}

function PointSpendingsTable({ accessToken }: { accessToken: string }) {
  const { ids, haveMore, loading, more } = usePointSpendings(accessToken)

  return (
    <>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            {ids === undefined ? (
              <tr><td colSpan={3}><Spinner size={16} /></td></tr>
            ) : ids.length === 0 ? (
              <tr><td colSpan={3}>You have not spent any points yet</td></tr>
            ) : (
              ids.map((id) => <PointSpendingRow key={id} id={id} accessToken={accessToken} />)
            )}
          </tbody>
        </table>
      </div>
      <div style={{ marginLeft: "8px" }}>
        {haveMore && ids !== undefined && (
          loading ? <Spinner size={16} /> : (
            <button className="card-link" onClick={more} style={{ background: "none", border: "none", cursor: "pointer" }}>
              Load more
            </button>
          )
        )}
      </div>
    </>
  )
}

export default function PointsPage() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const { data: points } = usePoints(accessToken)
  const balance = points ? points.topups - points.spendings : undefined

  const [tab, setTab] = useState<"earnings" | "spendings">("earnings")

  return (
    <div className="page">
      <h1 className="page-title">Reward Points</h1>

      <div className="credits-hero">
        <span className="credits-balance">
          {balance != null
            ? balance
            : <Spinner size={24} />}
        </span>
        <span className="credits-label">points available</span>
        {points && points.pending > 0
          ? <span className="credits-label">{points.pending} points pending</span>
          : null}
      </div>

      <div className="history-tabs">
        <button
          className={`history-tab${tab === "earnings" ? " history-tab-active" : ""}`}
          onClick={() => setTab("earnings")}
        >
          Earnings
        </button>
        <button
          className={`history-tab${tab === "spendings" ? " history-tab-active" : ""}`}
          onClick={() => setTab("spendings")}
        >
          Spendings
        </button>
      </div>

      {tab === "earnings"
        ? <PointTopupsTable accessToken={accessToken} />
        : <PointSpendingsTable accessToken={accessToken} />}
    </div>
  )
}
